import * as C from "../config/constants.js";
import { mergeTierImageUrl } from "../assets/merge-tier-urls.js";
import {
  EVOLUTION_TIER_FADED,
  EVOLUTION_TIER_UNDISCOVERED,
  getEvolutionTierVisualState,
  isRunMaxTierChip,
} from "./evolution-tier-visual.js";

/** Creature chips on the ring before the secret tier is exposed. */
export var RING_BASE_SLOTS = 10;
/** Max dial chips (10 + the secret "?" / Dragon chip once T-rex is unlocked). */
export var RING_MAX_CHIPS = 11;
/** Arc positions: 10 chips + 1 empty tail slot (matches SVG arrow gap). */
export var RING_ARC_SLOTS = 11;

/**
 * @param {object[]} chain
 * @param {Record<number, boolean>} unlocked
 * @param {function(number, object[]): boolean} [skip]
 */
export function getRingMaxNodes(chain, unlocked, skip) {
  if (!chain || chain.length === 0) return RING_BASE_SLOTS;
  if (chain.length <= RING_BASE_SLOTS) return Math.min(RING_BASE_SLOTS, chain.length);
  var finalIndex = chain.length - 1;
  if (skip && skip(finalIndex, chain)) return RING_BASE_SLOTS;
  var gateTier = chain[finalIndex - 1];
  if (gateTier && unlocked && unlocked[gateTier.id]) return RING_MAX_CHIPS;
  return RING_BASE_SLOTS;
}

/**
 * @param {object[]} chain
 * @param {Record<number, boolean>} unlocked
 * @param {function(number, object[]): boolean} [skip]
 */
export function getRingArcSlots(chain, unlocked, skip) {
  return RING_ARC_SLOTS;
}

/**
 * Secret-tier chip mode once the penultimate tier is unlocked: "?" teaser chip
 * or the real dragon art on the 11th chip.
 *
 * @param {object[]} chain
 * @param {Record<number, boolean>} unlocked
 * @returns {"teaser"|"dragon"|null}
 */
export function getRingSecretOverlayMode(chain, unlocked) {
  if (!chain || chain.length <= RING_BASE_SLOTS) return null;
  var finalIndex = chain.length - 1;
  var gateTier = chain[finalIndex - 1];
  var finalTier = chain[finalIndex];
  if (!gateTier || !finalTier) return null;
  if (!unlocked || !unlocked[gateTier.id]) return null;
  return unlocked[finalTier.id] ? "dragon" : "teaser";
}

/**
 * True when the chip at chain index is the hidden-final "?" teaser chip.
 *
 * @param {object[]} chain
 * @param {number} chainIndex
 * @param {Record<number, boolean>} unlocked
 */
export function isSecretTeaserChip(chain, chainIndex, unlocked) {
  if (!chain || chain.length <= RING_BASE_SLOTS) return false;
  if (chainIndex !== chain.length - 1) return false;
  var tier = chain[chainIndex];
  if (!tier) return false;
  return !(unlocked && unlocked[tier.id]);
}

/**
 * Chip spacing along the arc. When an 11th chip appears, squeeze into the span
 * that 10 chips used so the trailing empty slot stays clear of the SVG arrow.
 *
 * @param {number} chipCount
 */
export function getRingChipStepDeg(chipCount) {
  var baseMaxIndex = RING_BASE_SLOTS - 1;
  var baseStep = 360 / RING_ARC_SLOTS;
  if (chipCount <= RING_BASE_SLOTS) return baseStep;
  var squeezedSpan = baseMaxIndex * baseStep;
  return chipCount > 1 ? squeezedSpan / (chipCount - 1) : baseStep;
}

/**
 * @param {object[]} chain
 * @param {Record<number, boolean>} unlocked
 * @param {function(number, object[]): boolean} [skip]
 * @param {boolean} [suppressSecretChip] render as if the secret chip is not exposed yet
 * @returns {{ index: number, tier: object }[]}
 */
export function buildRingTiers(chain, unlocked, skip, suppressSecretChip) {
  var maxNodes = suppressSecretChip
    ? Math.min(RING_BASE_SLOTS, getRingMaxNodes(chain, unlocked, skip))
    : getRingMaxNodes(chain, unlocked, skip);
  var ringTiers = [];
  for (var i = 0; i < chain.length && ringTiers.length < maxNodes; i++) {
    if (skip && skip(i, chain)) continue;
    ringTiers.push({ index: i, tier: chain[i] });
  }
  return ringTiers;
}

/**
 * Standalone secret "?" chip node — appended by the reveal ceremony when the
 * dial squeezes from 10 to 11 chips before the final populate.
 *
 * @param {Document} doc
 * @param {number} slotIndex position on the arc (--evo-i)
 * @param {number} tierIndex chain index for data-tier-index
 * @returns {HTMLElement}
 */
export function createRingSecretChipNode(doc, slotIndex, tierIndex) {
  doc = doc || document;
  var node = doc.createElement("span");
  node.className = "evo-ring__node evo-ring__node--undiscovered evo-ring__node--secret";
  node.style.setProperty("--evo-i", String(slotIndex));
  node.setAttribute("data-tier-index", String(tierIndex));
  appendRingQuestionMark(node, doc);
  return node;
}

/** @param {HTMLElement} node @param {Document} [doc] */
export function appendRingQuestionMark(node, doc) {
  doc = doc || document;
  var mark = doc.createElement("span");
  mark.className = "evo-ring__node-mark";
  mark.textContent = "?";
  node.appendChild(mark);
}

/**
 * Hub count denominator matches visible ring chips (10, or 11 with the secret chip).
 *
 * @param {object[]} chain
 * @param {{ index: number, tier: object }[]} ringTiers
 * @param {function(number, object[]): boolean} [skip]
 */
export function getRingCountTotal(chain, ringTiers, skip) {
  if (ringTiers && ringTiers.length > 0) return ringTiers.length;
  if (!chain || chain.length === 0) return RING_BASE_SLOTS;
  return getRingMaxNodes(chain, {}, skip);
}

/**
 * @param {Record<number, boolean>} unlockedTiers
 * @param {object[]} chain
 * @param {function(number, object[]): boolean} [skip]
 */
export function countRingUnlocked(unlockedTiers, chain, skip) {
  var ringTiers = buildRingTiers(chain, unlockedTiers || {}, skip);
  var n = 0;
  for (var i = 0; i < ringTiers.length; i++) {
    if (unlockedTiers[ringTiers[i].tier.id]) n++;
  }
  return n;
}

/**
 * Stable signature for evolution-ring DOM state — skip full rebuild when unchanged.
 *
 * @param {{
 *   unlockedTiers: Record<number, boolean>,
 *   chain: object[],
 *   currentTierId?: number,
 *   skipTier?: function(number, object[]): boolean,
 * }} state
 */
export function buildEvolutionRingSignature(state) {
  state = state || {};
  var chain = state.chain || [];
  var unlocked = state.unlockedTiers || {};
  var skip = state.skipTier;
  var ringTiers = buildRingTiers(chain, unlocked, skip);
  var parts = [String(ringTiers.length)];
  for (var i = 0; i < ringTiers.length; i++) {
    var tier = ringTiers[i].tier;
    parts.push(String(tier.id) + ":" + (unlocked[tier.id] ? "1" : "0"));
  }
  parts.push("cur=" + (state.currentTierId != null ? state.currentTierId : ""));
  parts.push("secret=" + (getRingSecretOverlayMode(chain, unlocked) || "none"));
  return parts.join("|");
}

/**
 * @param {HTMLElement} mount
 * @param {{
 *   unlockedTiers: Record<number, boolean>,
 *   chain: object[],
 *   currentTierId?: number,
 *   skipTier?: function(number, object[]): boolean,
 *   suppressSecretChip?: boolean,
 *   pendingTierIndex?: number|null,
 *   hiddenTierIndex?: number|null,
 *   showLockedQuestionMarks?: boolean,
 * }} state
 */
export function renderEvolutionRing(mount, state) {
  if (!mount) return;
  state = state || {};
  var chain = state.chain || [];
  var unlocked = state.unlockedTiers || {};
  var skip = state.skipTier;
  var ringTiers = buildRingTiers(chain, unlocked, skip, state.suppressSecretChip);
  var showLockedQuestionMarks = !!state.showLockedQuestionMarks;

  // Hub creature = highest tier reached THIS run (currentTierId). Collection unlocks
  // only affect chip silhouettes, not the centre count.
  var hasRunTier = state.currentTierId != null && state.currentTierId !== undefined;
  var currentIdx = 0;
  if (hasRunTier) {
    for (var c = 0; c < ringTiers.length; c++) {
      if (ringTiers[c].tier.id === state.currentTierId
        && !isSecretTeaserChip(chain, ringTiers[c].index, unlocked)) {
        currentIdx = c;
        break;
      }
    }
  } else {
    for (var u = 0; u < ringTiers.length; u++) {
      if (unlocked[ringTiers[u].tier.id]) currentIdx = u;
    }
  }
  // Hub N/total tracks the centre creature; total matches ring chip count.
  var unlockedCount = currentIdx + 1;
  var ringCountTotal = getRingCountTotal(chain, ringTiers, skip);

  mount.innerHTML = "";
  mount.classList.add("evo-ring--no-idle-pulse");

  var panel = document.createElement("div");
  panel.className = "card evo-panel";

  var arcSlots = getRingArcSlots(chain, unlocked, skip);
  var chipStepDeg = getRingChipStepDeg(ringTiers.length);

  var ring = document.createElement("div");
  ring.className = "evo-ring";
  ring.style.setProperty("--evo-slots", String(arcSlots));
  ring.style.setProperty("--evo-chip-step", chipStepDeg + "deg");
  ring.style.setProperty("--evo-count", String(ringTiers.length));
  ring.style.setProperty("--evo-unlocked", String(unlockedCount));
  ring.style.setProperty("--evo-current", String(currentIdx));

  var bg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  bg.setAttribute("class", "evo-ring__bg");
  bg.setAttribute("viewBox", "0 0 512 512");
  var ringGrad =
    "background:conic-gradient(from 90deg,var(--evo-grad-1,#45C4F0) 0deg,var(--evo-grad-2,#3CB4DC) 58.8462deg,var(--evo-grad-3,#3ED598) 138.462deg,var(--evo-grad-4,#7CC83A) 226.731deg,var(--evo-grad-5,#F0A714) 315deg,var(--evo-grad-1,#45C4F0) 360deg);height:100%;width:100%;opacity:1";
  bg.innerHTML =
    "<defs><clipPath id=\"evoRingArcClipShipped\"><path d=\"M74.9807 74.9807C33.1009 116.86 7.03796 171.966 1.23271 230.908C-4.57254 289.849 10.2391 348.981 43.1438 398.226C76.0485 447.471 125.01 483.784 181.687 500.977C238.364 518.169 299.248 515.178 353.967 492.513C408.686 469.848 453.852 428.911 481.772 376.678C509.691 324.444 518.636 264.146 507.081 206.057C495.526 147.968 464.188 95.6825 418.405 58.1093C372.622 20.5362 315.227 4.47151e-06 256 0L256 97.6567C292.634 97.6567 328.134 110.359 356.452 133.599C384.77 156.839 404.154 189.179 411.301 225.109C418.448 261.038 412.915 298.335 395.646 330.643C378.377 362.95 350.44 388.271 316.595 402.29C282.75 416.309 245.091 418.159 210.035 407.525C174.979 396.891 144.695 374.431 124.342 343.971C103.99 313.511 94.8284 276.937 98.4192 240.48C102.01 204.022 118.131 169.938 144.034 144.034L74.9807 74.9807Z\"/></clipPath></defs><g clip-path=\"url(#evoRingArcClipShipped)\"><g transform=\"matrix(0 -0.256 0.256 0 256 256)\"><foreignObject x=\"-1003.91\" y=\"-1003.91\" width=\"2007.81\" height=\"2007.81\"><div xmlns=\"http://www.w3.org/1999/xhtml\" style=\"" + ringGrad + "\"></div></foreignObject></g></g><path d=\"M131.737 87.3424L144.021 144.043L75.0363 75.0579L131.737 87.3424Z\" fill=\"var(--evo-grad-5,#F0A714)\"/>";
  ring.appendChild(bg);

  var nodes = document.createElement("div");
  nodes.className = "evo-ring__nodes";
  for (var n = 0; n < ringTiers.length; n++) {
    var entry = ringTiers[n];
    var node = document.createElement("span");
    node.className = "evo-ring__node";
    node.style.setProperty("--evo-i", String(n));
    node.setAttribute("data-tier-index", String(entry.index));
    if (state.pendingTierIndex != null && entry.index === state.pendingTierIndex) {
      node.classList.add("evo-ring__node--undiscovered");
      node.classList.add("evo-ring__node--reveal-pending");
    } else if (isSecretTeaserChip(chain, entry.index, unlocked)) {
      node.classList.add("evo-ring__node--undiscovered");
      node.classList.add("evo-ring__node--secret");
      appendRingQuestionMark(node);
    } else {
      var visual = getEvolutionTierVisualState(entry.tier.id, unlocked, state.currentTierId);
      if (visual === EVOLUTION_TIER_UNDISCOVERED) {
        node.classList.add("evo-ring__node--undiscovered");
        if (showLockedQuestionMarks) {
          node.classList.add("evo-ring__node--secret");
          appendRingQuestionMark(node);
        }
      } else if (visual === EVOLUTION_TIER_FADED) {
        node.classList.add("evo-ring__node--faded");
      }
      if (visual !== EVOLUTION_TIER_UNDISCOVERED || !showLockedQuestionMarks) {
        var nimg = document.createElement("img");
        nimg.src = mergeTierImageUrl(entry.tier.assetId || "cell");
        nimg.alt = entry.tier.name || "";
        node.appendChild(nimg);
      }
    }
    if (state.hiddenTierIndex != null && entry.index === state.hiddenTierIndex) {
      node.classList.add("evo-reveal-chip-hidden");
    }
    nodes.appendChild(node);
  }
  ring.appendChild(nodes);

  var hubEntry = ringTiers[currentIdx];
  var hubTier = hubEntry ? hubEntry.tier : chain[0];
  if (hubEntry && isSecretTeaserChip(chain, hubEntry.index, unlocked)) {
    hubTier = ringTiers[0] ? ringTiers[0].tier : chain[0];
  }
  var hubVisual = getEvolutionTierVisualState(hubTier.id, unlocked, state.currentTierId);
  var hub = document.createElement("div");
  hub.className = "evo-ring__hub";
  if (isRunMaxTierChip(hubTier.id, hubVisual, state.currentTierId)) {
    hub.classList.add("evo-ring__hub--run-max");
  }
  var hubImg = document.createElement("img");
  hubImg.src = mergeTierImageUrl(hubTier.assetId || "cell");
  hubImg.alt = "";
  hub.appendChild(hubImg);
  var countEl = document.createElement("span");
  countEl.className = "evo-ring__count";
  countEl.innerHTML = unlockedCount + "<span class=\"evo-ring__count-sep\">/</span>" + ringCountTotal;
  hub.appendChild(countEl);
  ring.appendChild(hub);

  panel.appendChild(ring);
  mount.appendChild(panel);
}
